/**
 * MessageDashboardPresenter
 * --
 */
import React from 'react';
import { Content, Title, Row, Col, ExpandableContainer } from 'components';
import { Divider, Button, Table, Radio, Spin, Statistic } from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import {
  CartesianGrid,
  Tooltip,
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Legend,
  Bar,
  Rectangle,
} from 'recharts';
import { stringToMoneyFormat } from 'utils';

/**----------------------------------------
 * [Component] 총관리자: 주문대시보드 - 프레젠터
 * ----------------------------------------
 */
const MessageDashboardPresenter = ({
  loading,
  chartData,
  statisticsType,
  setStatisticsType,
  handleGetCartList,
}) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */

  /* ===== VARIABLE ===== */
  const formItem = [
    {
      label: '기간설정',
      form: [
        {
          input: (
            <>
              <Radio.Group
                size="large"
                defaultValue="month"
                buttonStyle="solid"
                value={statisticsType}
                onChange={(e) => setStatisticsType(e.target.value)}
                style={{ marginRight: 12 }}
              >
                <Radio.Button value="today">오늘</Radio.Button>
                <Radio.Button value="week">1주일(7일)</Radio.Button>
                <Radio.Button value="month">1개월(30일)</Radio.Button>
                <Radio.Button value="3month">3개월(90일)</Radio.Button>
              </Radio.Group>
            </>
          ),
        },
      ],
    },
  ];

  /* ===== FUNCTION ===== */
  /* ===== HOOKS ===== */

  /*  */
  /* 상품별 데이터 */
  const productChart = chartData?.product
    ? Object.keys(chartData.product).map((item) => ({
        name: chartData.product[item].label,
        value: chartData.product[item].data.length,
        ...chartData.product[item],
      }))
    : [];

  /* ===== RENDER ===== */
  // JSX: start
  return (
    <Spin spinning={loading}>
      <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
        <Row>
          <Col x={24}>
            <Title size={3} style={{ marginBottom: 12 }}>
              쿠폰 대시보드
            </Title>

            {/* 검색필터 */}
            <Content
              maxWidth={'100%'}
              padding={0}
              // backgroundColor={'none'}
              style={{ marginBottom: 5 }}
            >
              <Row>
                <Col x={19}>
                  <ExpandableContainer borderRadius={4} padding={20}>
                    {formItem.map((_item, _index) => (
                      <div
                        key={`form_${_index}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '10px 0px',
                        }}
                      >
                        <div
                          style={{ width: 120, fontSize: 13, fontWeight: 400 }}
                        >
                          <label>{_item.label}</label>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {_item.form.map((_subitem, _subkey) => (
                            <React.Fragment key={`input_${_subkey}`}>
                              {_subitem.input}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    ))}
                  </ExpandableContainer>
                </Col>
                {/* === === */}
                <Col
                  x={5}
                  style={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    display: 'flex',
                  }}
                >
                  <Button
                    size="large"
                    type="primary"
                    onClick={() => handleGetCartList()}
                  >
                    <SearchOutlined /> 검색
                  </Button>
                  &nbsp;
                  <Button
                    size="large"
                    // onClick={handleResetFilter}
                  >
                    <ReloadOutlined /> 초기화
                  </Button>
                </Col>
              </Row>
            </Content>
            {/* 검색필터 끝 */}

            {/* === 현황 요약: start === */}
            <Content
              fullWidth
              padding={0}
              backgroundColor={'none'}
              style={{
                marginBottom: 5,
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              {[
                {
                  title: '진행중인 쿠폰',
                  value: stringToMoneyFormat(10),
                  suffix: '건',
                },
                {
                  title: '쿠폰사용량',
                  value: stringToMoneyFormat(10),
                  suffix: '개',
                },
                {
                  title: '쿠폰매출액',
                  value: stringToMoneyFormat(10),
                  suffix: '개',
                },
                {
                  title: '종료된 쿠폰',
                  value: stringToMoneyFormat(10),
                  suffix: '개',
                },
              ].map((_item, _index) => (
                <div
                  key={`${_item.label}-${_index}`}
                  className="boxShadow"
                  style={{
                    background: '#FFFFFF',
                    borderRadius: 4,
                    width: `90%`,
                    margin: '0 2px',
                    padding: '24px 15px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Statistic
                    className="cursorOverOpacity"
                    direction={'row'}
                    size="large"
                    title={_item.title}
                    value={_item.value}
                    suffix={_item.suffix}
                    // style={{ justifyContent: 'center' }}
                    valueStyle={{
                      color: '#1677ff',
                      textDecoration: 'underline !important',
                    }}
                  />
                  <div>
                    <Button shape="circle">
                      <ExportOutlined />
                    </Button>
                  </div>
                </div>
              ))}
            </Content>
            {/* === 현황 요약: end === */}

            {/* ===== 차트: start ===== */}
            <Row gutter={[5, 5]}>
              {/* === 상품별: start === */}
              <Col x={12}>
                <Content
                  maxWidth={'100%'}
                  borderRadius={4}
                  // padding={'14px 5px'}
                  height={570}
                >
                  <Title size={5} style={{ marginBottom: 12 }}>
                    쿠폰별 사용량
                  </Title>
                  <Divider style={{ margin: 0 }} />
                  <ResponsiveContainer
                    width="100%"
                    height={465}
                    style={{ marginTop: 25 }}
                  >
                    <BarChart
                      width={500}
                      // height={300}
                      data={[
                        {
                          name: '2024.10',
                          Review: 2000,
                          ProductOrder: 9800,
                          amt: 2290,
                        },
                        {
                          name: '2024.11',
                          Review: 2780,
                          ProductOrder: 3908,
                          amt: 2000,
                        },
                        {
                          name: '2024.12',
                          Review: 1890,
                          ProductOrder: 4800,
                          amt: 2181,
                        },
                        {
                          name: '2025.01',
                          Review: 2390,
                          ProductOrder: 3800,
                          amt: 2500,
                        },
                        {
                          name: '2025.02',
                          Review: 3490,
                          ProductOrder: 4300,
                          amt: 2100,
                        },
                      ]}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey="ProductOrder"
                        fill="#8884d8"
                        activeBar={<Rectangle fill="pink" stroke="blue" />}
                      />
                      <Bar
                        dataKey="Review"
                        fill="#82ca9d"
                        activeBar={<Rectangle fill="gold" stroke="purple" />}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Content>
              </Col>
              {/* === 상품별: end === */}

              {/* === 내역: start === */}
              <Col x={12}>
                <Content
                  maxWidth={'100%'}
                  height={570}
                  borderRadius={4}
                  // padding={'14px 5px'}
                >
                  <Title size={5} style={{ marginBottom: 12 }}>
                    구매상품(Pie차트)
                  </Title>

                  <Table
                    bordered
                    // size="small"
                    scroll={{ x: 650, y: 450 }}
                    pagination={false}
                    dataSource={
                      productChart
                        .sort((a, b) => a.value - b.value)
                        .reverse() ?? []
                    }
                    columns={[
                      {
                        title: '날짜',
                        dataIndex: 'created_at',
                        width: 150,
                        align: 'center',
                      },
                      {
                        title: '주문건수',
                        dataIndex: 'value',
                        width: 120,
                        align: 'center',
                        render: (value) => `${stringToMoneyFormat(value)}`,
                      },
                      {
                        title: '주문금액',
                        dataIndex: 'price',
                        width: 150,
                        align: 'center',
                        render: (value) => `${stringToMoneyFormat(value)}원`,
                      },
                      {
                        title: '환불금액',
                        dataIndex: 'price',
                        width: 150,
                        align: 'center',
                        render: (value) => `${stringToMoneyFormat(value)}원`,
                      },
                    ]}
                  />
                </Content>
              </Col>
              {/* === 내역: end === */}
            </Row>
            {/* ===== 차트: end ===== */}
          </Col>
        </Row>
      </Content>
    </Spin>
  ); // JSX: end
};

export default MessageDashboardPresenter;
